"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchCatalogAndContentData = exports.toLowerCase = exports.formatProductContent = exports.getProductModifiers = exports.getVariantAttributes = exports.transformAttributeValue = exports.formatProduct = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const config_1 = require("./config");
const catalog_1 = require("../services/catalog");
const content_1 = require("../services/content");
const pattern_library_modules_cart_1 = require("@saatva-bits/pattern-library.modules.cart");
/**
 * Adds sortKeys and adds product and compare URIs
 * @param {object} product
 * @returns
 */
const formatProductDetails = (product) => {
    let details = {};
    product.details &&
        product.details.forEach((detail, index) => {
            const key = detail.key;
            const name = detail.name;
            const features = (0, omit_1.default)(detail, ['key', 'name']);
            // This key (feature, firmness) has been added so we push into content array!
            if (details[key] && details[key].content && details[key].content.length > 0) {
                details[key].content.push(features);
            }
            else {
                details = {
                    ...details,
                    [key]: {
                        sortOrder: index,
                        key,
                        name,
                        content: [features]
                    }
                };
            }
        });
    const detailsList = Object.values(details).sort((a, b) => a.sortOrder - b.sortOrder);
    return detailsList;
};
const filterDetails = (details = [], keys = []) => {
    if (!keys.length) {
        return details;
    }
    return details.filter(({ key }) => keys.includes(key));
};
/**
 * Merges prismic product content with catalog product data
 * @param {object} catalogProduct
 * @param {object} contentProduct
 * @param {string[]} keys
 * @returns
 */
const formatProduct = (catalogProduct, contentProduct, keys = [], selectedProduct = {}) => {
    /*
        copy of catalogProduct with more elements on the QTY dropdown values
        to allow selection of up to 10 items from detail modal
    */
    const newCatalogProduct = {
        ...catalogProduct,
        options: catalogProduct.options.map((opt) => {
            if (opt.code === 'quantity') {
                return getQuantityOption();
            }
            return opt;
        })
    };
    const filterdContent = {
        ...contentProduct,
        details: filterDetails(contentProduct.details, keys)
    };
    const pillowBundleOverrides = getPillowBundleOverrides(catalogProduct, selectedProduct);
    return {
        ...newCatalogProduct,
        ...contentProduct,
        ...pillowBundleOverrides,
        details: formatProductDetails(filterdContent),
        hasLearnMoreCta: false
    };
};
exports.formatProduct = formatProduct;
/**
 * Get quantity option
 * @param {Number} [maxQuantity=10]
 * @returns {Object}
 */
const getQuantityOption = (maxQuantity = 10) => {
    const values = [];
    for (let i = 1; i <= maxQuantity; i++) {
        values.push({
            code: i.toString(),
            label: i.toString(),
            swatch: null
        });
    }
    return {
        code: 'quantity',
        displayName: 'Qty',
        displayType: null,
        default: 1,
        values
    };
};
/**
 * For Pillow Pair bundles some values need to be updated as the default
 * information in the Catalog Service is intended for single products
 * @param {*} catalogProduct
 * @param {*} selectedProduct
 * @returns {Object}
 */
const getPillowBundleOverrides = (catalogProduct = {}, selectedProduct = {}) => {
    if ((0, pattern_library_modules_cart_1.isPillowBundle)(selectedProduct.sku)) {
        // Get only bundle variants
        const variants = catalogProduct.variants.filter((variant) => (0, pattern_library_modules_cart_1.isPillowBundle)(variant.sku)) || [];
        // Get price range for pillow pairs
        const prices = variants.map(({ price }) => price).sort((prev, next) => prev - next);
        return {
            variants,
            lowestPrice: prices[0],
            highestPrice: prices[prices.length - 1]
        };
    }
    return {};
};
const STANDARD_QUEEN = 'Standard/Queen';
const SHORT_MATTRESS = '11.5';
const TALL_MATTRESS = '14.5';
/**
 * Transform attribute value
 * Source: service-cart-checkout/app/dataSources/CatalogService/index.js:103
 * @param {string} value
 * @returns {string}
 */
const transformAttributeValue = (value) => {
    if (value === STANDARD_QUEEN) {
        return 'Queen';
    }
    if (value === SHORT_MATTRESS) {
        return '11.5"';
    }
    if (value === TALL_MATTRESS) {
        return '14.5"';
    }
    return value;
};
exports.transformAttributeValue = transformAttributeValue;
/**
 * Get attributes of the selected variant
 * @param {Object} selectedProduct
 * @param {Object} productData
 * @returns {Object.<string, string>}
 */
const getVariantAttributes = (selectedProduct, productData) => {
    const selectedProductAttributeValues = selectedProduct.productAttributes
        ? selectedProduct.productAttributes.map(({ value }) => value)
        : []; // Recommended product doesn't have productAttributes
    const selectedVariant = productData.variants.find((variant) => {
        const variantAttributes = variant.attributes.map((attr) => (0, exports.transformAttributeValue)(attr.value));
        // Check if the variant is in stock then check if the selected product attributes are included in the variant attributes
        return (variant.inStock &&
            selectedProductAttributeValues.every((value) => variantAttributes.includes(value)));
    });
    if (selectedVariant) {
        return selectedVariant.attributes.reduce((acc, attr) => {
            acc[attr.code] = attr.value;
            return acc;
        }, {});
    }
    return {};
};
exports.getVariantAttributes = getVariantAttributes;
/**
 * Get the product modifiers
 * @param {Object} product Selected cart product
 * @returns {string[]} List of product modifiers
 */
const getProductModifiers = (product) => {
    const isBundle = (0, pattern_library_modules_cart_1.isPillowBundle)(product.sku) && (0, pattern_library_modules_cart_1.isPillowSku)(product.productCode);
    let MODIFIERS = [
        config_1.catalogModifiers.EXCLUDE_QUILT_SETS,
        config_1.catalogModifiers.PRE_CART,
        config_1.catalogModifiers.EXCLUDE_PILLOW_BUNDLES
    ];
    // allows the catalog service to return all variants including pillow pair bundles
    if (isBundle) {
        return [config_1.catalogModifiers.EXCLUDE_QUILT_SETS];
    }
    return MODIFIERS;
};
exports.getProductModifiers = getProductModifiers;
/**
 * Build basic product data for the modal
 * @param {Object | undefined} content Product data from content service
 * @param {Object} selectedProduct Selected product
 * @param {Object} catalogData Product data from catalog service
 * @returns {Object}
 */
const formatProductContent = (content, selectedProduct = {}, catalogData = {}) => {
    var _a;
    let formattedContent = content ? { ...content } : {};
    // Replace name to display only for pillow pair bundles
    if ((0, pattern_library_modules_cart_1.isPillowBundle)(selectedProduct.sku)) {
        formattedContent.title = selectedProduct.genericName;
    }
    // If a product doesn't have content or the Content Service is experiencing issues,
    // provide the minimum necessary data to make the modal work.
    if (!content) {
        formattedContent = {
            title: selectedProduct.genericName || selectedProduct.name,
            subtitle: (_a = catalogData === null || catalogData === void 0 ? void 0 : catalogData.content) === null || _a === void 0 ? void 0 : _a.shortDescription
        };
    }
    return formattedContent;
};
exports.formatProductContent = formatProductContent;
/**
 * Safely convert a string to lowercase
 * @param {string} str
 * @returns {string}
 */
const toLowerCase = (str = '') => {
    if (!str || typeof str !== 'string') {
        return '';
    }
    return str.toLowerCase();
};
exports.toLowerCase = toLowerCase;
const fetchCatalogAndContentData = async (products = [], apiRoutes = {}) => {
    if (products.length === 0)
        return { catalogData: [], contentData: [] };
    try {
        const productCodes = products.map(product => product.productCode);
        const [nonPillowPairProducts, pillowPairProducts] = products.reduce((acc, item) => {
            acc[(0, pattern_library_modules_cart_1.isPillowSku)(item.productCode) && (0, pattern_library_modules_cart_1.isPillowBundle)(item.sku) ? 1 : 0].push(item);
            return acc;
        }, [[], []]);
        const nonPillowPairModifiers = [config_1.catalogModifiers.EXCLUDE_QUILT_SETS, config_1.catalogModifiers.PRE_CART, config_1.catalogModifiers.EXCLUDE_PILLOW_BUNDLES];
        const pillowPairModifiers = [config_1.catalogModifiers.EXCLUDE_QUILT_SETS];
        // Get ProductData from the Catalog Service for non pillow pair products
        const nonPillowPairCatalogData = await (0, catalog_1.getProductsData)(apiRoutes, {
            productCodes: nonPillowPairProducts.map(product => product.productCode),
            modifiers: nonPillowPairModifiers
        });
        // Get ProductData from the Catalog Service for pillow pair products
        const pillowPairCatalogData = await (0, catalog_1.getProductsData)(apiRoutes, {
            productCodes: pillowPairProducts.map(product => product.productCode),
            modifiers: pillowPairModifiers
        });
        // Get ProductContentData from the Content Service for all products
        const contentData = await (0, content_1.getProductContentDataByTag)(apiRoutes, productCodes);
        return {
            catalogData: [...nonPillowPairCatalogData, ...pillowPairCatalogData],
            contentData: contentData
        };
    }
    catch (error) {
        console.error(`Error fetching catalog and content data: ${error}`);
        return { catalogData: [], contentData: [] };
    }
};
exports.fetchCatalogAndContentData = fetchCatalogAndContentData;
//# sourceMappingURL=product.js.map